import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, FileDown, FileSearch, Loader2 } from "lucide-react";
import { api, downloadFile, errMsg } from "@/lib/api";
import { VergunningenPanel } from "@/components/VergunningenPanel";
import { PageTitle, StatusBadge } from "@/components/StatusBadge";
import { CompareViewer } from "@/components/CompareViewer";
import { AiAssessment } from "@/components/AiAssessment";
import { StatusActions } from "@/components/StatusActions";

const History = ({ items = [] }) => (
  <div className="space-y-3" data-testid="melding-history">
    {[...items].reverse().map((h, i) => (
      <div key={i} className="flex gap-3 text-xs">
        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
        <div>
          <div className="flex items-center gap-2"><StatusBadge status={h.status} /><span className="text-slate-500 font-mono">{new Date(h.at).toLocaleString("nl-NL")}</span></div>
          <div className="text-slate-400 mt-1">{h.by}{h.notitie && ` — ${h.notitie}`}</div>
        </div>
      </div>
    ))}
  </div>
);

export default function MeldingDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: m } = useQuery({ queryKey: ["melding", id], queryFn: () => api.get(`/meldingen/${id}`).then((r) => r.data) });
  const [busy, setBusy] = useState(null);
  if (!m) return <div className="text-slate-500 font-mono text-xs">LADEN…</div>;
  const byReg = Object.fromEntries((m.objecten || []).map((o) => [o.regio, o]));
  const objecten = (m.analysis?.ai?.objecten || m.objecten).map((o) => ({ ...o, ...(byReg[o.regio] ? { vergunning_match: byReg[o.regio].vergunning_match, vergunning_niveau: byReg[o.regio].vergunning_niveau } : {}) }));
  const refresh = (d) => { qc.setQueryData(["melding", id], d); qc.invalidateQueries({ queryKey: ["meldingen"] }); qc.invalidateQueries({ queryKey: ["stats"] }); qc.invalidateQueries({ queryKey: ["vergunningen"] }); };
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "pdf") await downloadFile(`/meldingen/${id}/rapport.pdf`, `inspectierapport_${m.address?.postcode}_${m.address?.huisnummer}_${id.slice(0, 8)}.pdf`);
      else {
        const { data } = await api.post(`/meldingen/${id}/check-vergunningen`);
        refresh(data);
        toast.success(data.status === "vergund" ? "Alle objecten vergund — melding afgehandeld" : "Vergunningen gecontroleerd");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <Link to="/meldingen" className="text-xs text-slate-400 hover:text-white flex items-center gap-1 mb-4" data-testid="back-to-meldingen"><ArrowLeft className="h-3.5 w-3.5" /> Meldingen</Link>
      <PageTitle eyebrow={`Melding · perceel ${m.perceel_aanduiding} · ${m.baseline_year} → ${m.upload_id ? m.compare_label : m.compare_year}`} title={m.weergavenaam}>
        <StatusBadge status={m.status} testId="melding-status" />
        <button data-testid="melding-check-permits-btn" onClick={() => run("verg")} disabled={!!busy} className="h-8 px-3 rounded-md border border-slate-700 text-xs hover:border-lime-500 flex items-center gap-1.5">
          {busy === "verg" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSearch className="h-3.5 w-3.5" />} Vergunningen controleren
        </button>
        <button data-testid="melding-pdf-btn" onClick={() => run("pdf")} disabled={!!busy} className="h-8 px-3 rounded-md bg-blue-600 hover:bg-blue-500 text-xs flex items-center gap-1.5">
          {busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} PDF-rapport
        </button>
        <Link to={`/adressen/${m.address_id}`} className="h-8 px-3 grid place-items-center rounded-md border border-slate-700 text-xs hover:border-blue-500" data-testid="melding-address-link">Naar adres & kaart</Link>
      </PageTitle>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 border border-slate-800 bg-[#111827]/80 rounded-lg p-4">
          {m.analysis && <CompareViewer addressId={m.address_id} analysis={m.analysis} highlight={m.objecten.map((o) => o.regio)} />}
        </div>
        <div className="lg:col-span-4 space-y-6">
          <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5">
            <AiAssessment samenvatting={m.samenvatting} objecten={objecten} advies={m.advies} prioriteit={m.prioriteit} aiError={m.ai_error} />
          </div>
          <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5">
            <h3 className="font-display font-semibold mb-3">Beoordeling</h3>
            <StatusActions melding={m} onUpdated={refresh} />
          </div>
          <VergunningenPanel addressId={m.address_id} />
          <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5">
            <h3 className="font-display font-semibold mb-3">Historie</h3>
            <History items={m.history} />
          </div>
        </div>
      </div>
    </div>
  );
}
